import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

export interface ModuleSection {
  id: string;
  module_id: string;
  course_id: string;
  section_id: string;
  section_title: string;
  section_content: string;
  section_order: number;
  word_count: number;
  created_at: string;
  updated_at: string;
}

export interface DivisionStatus {
  id: string;
  module_id: string;
  course_id: string;
  original_word_count: number;
  division_tier: 'extreme' | 'heavy' | 'moderate';
  num_sections: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  processed_by?: string;
  processing_method?: 'manual' | 'semi-auto' | 'auto';
  error_message?: string;
  started_at?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

export class ContentDivisionService {
  /**
   * Fetch sections for a module (if divided)
   * Returns null if module hasn't been divided yet
   */
  static async getModuleSections(
    moduleId: string,
    courseId: string
  ): Promise<ModuleSection[] | null> {
    try {
      const { data, error } = await supabase
        .from('module_content_sections')
        .select('*')
        .eq('module_id', moduleId)
        .eq('course_id', courseId)
        .order('section_order');

      if (error) throw error;

      return data && data.length > 0 ? data : null;
    } catch (error) {
      logger.error('Failed to fetch module sections:', error);
      return null;
    }
  }

  /**
   * Get division status for a module
   */
  static async getDivisionStatus(
    moduleId: string,
    courseId: string
  ): Promise<DivisionStatus | null> {
    try {
      const { data, error } = await supabase
        .from('content_division_status')
        .select('*')
        .eq('module_id', moduleId)
        .eq('course_id', courseId)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 = not found

      return data as DivisionStatus | null;
    } catch (error) {
      logger.error('Failed to fetch division status:', error);
      return null;
    }
  }

  /**
   * Get all modules by division tier for admin dashboard
   */
  static async getModulesByTier(
    tier: 'extreme' | 'heavy' | 'moderate'
  ): Promise<DivisionStatus[]> {
    try {
      const { data, error } = await supabase
        .from('content_division_status')
        .select('*')
        .eq('division_tier', tier)
        .order('original_word_count', { ascending: false });

      if (error) throw error;

      return (data as DivisionStatus[]) || [];
    } catch (error) {
      logger.error(`Failed to fetch ${tier} tier modules:`, error);
      return [];
    }
  }

  /**
   * Get division statistics for dashboard
   */
  static async getDivisionStats() {
    try {
      // First, sync status with actual sections
      logger.info('🔄 Syncing division status before fetching stats...');
      await supabase.rpc('sync_division_status_with_sections');
      
      const { data, error } = await supabase
        .from('content_division_status')
        .select('division_tier, status');

      if (error) throw error;

      const stats = {
        extreme: { total: 0, completed: 0, pending: 0, processing: 0, failed: 0 },
        heavy: { total: 0, completed: 0, pending: 0, processing: 0, failed: 0 },
        moderate: { total: 0, completed: 0, pending: 0, processing: 0, failed: 0 },
        overall: { total: 0, completed: 0, pending: 0, processing: 0, failed: 0 }
      };

      data?.forEach((item: any) => {
        const tier = item.division_tier as 'extreme' | 'heavy' | 'moderate';
        const status = item.status as 'completed' | 'pending' | 'processing' | 'failed';
        
        stats[tier].total++;
        stats[tier][status]++;
        stats.overall.total++;
        stats.overall[status]++;
      });

      logger.info('📊 Division stats:', stats);
      return stats;
    } catch (error) {
      logger.error('Failed to fetch division stats:', error);
      return null;
    }
  }

  /**
   * Save divided content sections
   */
  static async saveSections(
    moduleId: string,
    courseId: string,
    sections: Omit<ModuleSection, 'id' | 'created_at' | 'updated_at'>[]
  ): Promise<boolean> {
    try {
      // First, delete existing sections for this module
      await supabase
        .from('module_content_sections')
        .delete()
        .eq('module_id', moduleId)
        .eq('course_id', courseId);

      // Insert new sections
      const { error } = await supabase
        .from('module_content_sections')
        .insert(sections);

      if (error) throw error;

      logger.info(`Saved ${sections.length} sections for module ${moduleId}`);
      return true;
    } catch (error) {
      logger.error('Failed to save module sections:', error);
      return false;
    }
  }

  /**
   * Update division status
   */
  static async updateStatus(
    moduleId: string,
    courseId: string,
    updates: Partial<Omit<DivisionStatus, 'id' | 'created_at' | 'updated_at'>>
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('content_division_status')
        .upsert({
          module_id: moduleId,
          course_id: courseId,
          ...updates
        } as any);

      if (error) throw error;

      logger.info(`Updated division status for module ${moduleId}`);
      return true;
    } catch (error) {
      logger.error('Failed to update division status:', error);
      return false;
    }
  }

  /**
   * Auto-detect section breaks in markdown content
   * Returns suggested sections based on H2/H3 headings
   */
  static autoDetectSections(content: string): {
    title: string;
    content: string;
    estimatedWords: number;
  }[] {
    const sections: { title: string; content: string; estimatedWords: number }[] = [];
    
    // Split by H2 headers (## )
    const h2Splits = content.split(/^## /gm);
    
    h2Splits.forEach((section, index) => {
      if (index === 0 && !section.trim().startsWith('#')) {
        // First section before any H2 - treat as overview
        if (section.trim()) {
          sections.push({
            title: 'Overview',
            content: section.trim(),
            estimatedWords: section.trim().split(/\s+/).length
          });
        }
        return;
      }

      // Extract title from first line
      const lines = section.split('\n');
      const title = lines[0].trim();
      const sectionContent = lines.slice(1).join('\n').trim();

      if (sectionContent) {
        sections.push({
          title,
          content: sectionContent,
          estimatedWords: sectionContent.split(/\s+/).length
        });
      }
    });

    return sections;
  }

  /**
   * Calculate optimal number of tabs based on word count
   */
  static calculateOptimalTabs(wordCount: number): number {
    if (wordCount >= 5000) return 5; // Extreme: 5 tabs
    if (wordCount >= 3000) return 4; // Heavy: 4 tabs
    if (wordCount >= 2000) return 3; // Moderate: 3 tabs
    return 1; // No division needed
  }
}
